// @ts-nocheck
import axios from 'axios';

import API_ENDPOINTS from '../const/ApiEndPoints';
import SwalToast from '../components/Common/swalToast';

const FORMAT_EXTENSION = {
  pdf: 'pdf',
  html: 'html',
  txt: 'txt',
};

const isMissing = (value) =>
  value === undefined || value === null || value === '';

/**
 * Reads a response header from Axios/browser header shapes.
 * @param {object} headers Response headers object.
 * @param {string} headerName Header name to read.
 * @returns {string} Header value, or an empty string when absent.
 */
const getHeader = (headers, headerName) => {
  if (!headers) return '';
  if (typeof headers.get === 'function') {
    return (
      headers.get(headerName) || headers.get(headerName.toLowerCase()) || ''
    );
  }
  return headers[headerName] || headers[headerName.toLowerCase()] || '';
};

/**
 * Converts a label/name into a safe fallback filename base.
 * @param {unknown} value Attribute label, name, or fallback value.
 * @returns {string} Sanitized filename part without unsafe characters.
 */
const sanitizeFileNamePart = (value) => {
  const safeValue = Array.from(String(value || 'html_attribute'))
    .filter((char) => char >= ' ')
    .join('');
  const fallback = safeValue
    .trim()
    .replace(/[<>:"/\\|?*]/g, '_')
    .replace(/\s+/g, '_');
  return fallback || 'html_attribute';
};

/**
 * Builds a fallback filename for the requested download format.
 * @param {string} attributeLabel Attribute label/name used as the filename base.
 * @param {string} format Selected download format.
 * @returns {string} Fallback filename with exactly one supported extension.
 */
const buildFallbackFileName = (attributeLabel, format) => {
  const extension = FORMAT_EXTENSION[format] || format;
  const baseName = sanitizeFileNamePart(attributeLabel).replace(
    /\.(html|pdf|txt)$/i,
    ''
  );
  return `${baseName}.${extension}`;
};

/**
 * Extracts a filename from a Content-Disposition header.
 * @param {string} contentDisposition Raw Content-Disposition header value.
 * @returns {string} Decoded filename, or an empty string when unavailable.
 */
const getFileNameFromContentDisposition = (contentDisposition) => {
  if (!contentDisposition) return '';

  const encodedMatch = contentDisposition.match(/filename\*=([^;]+)/i);
  if (encodedMatch?.[1]) {
    const encodedValue = encodedMatch[1].trim().replace(/^UTF-8''/i, '');
    try {
      return decodeURIComponent(encodedValue.replace(/^"|"$/g, ''));
    } catch (_error) {
      return encodedValue.replace(/^"|"$/g, '');
    }
  }

  const filenameMatch = contentDisposition.match(/filename="?([^";]+)"?/i);
  return filenameMatch?.[1]?.trim() || '';
};

const getMessageFromObject = (data) => {
  if (!data || typeof data !== 'object') return '';
  const message =
    data.msg ||
    data.message ||
    data.detail ||
    data.error ||
    data.errors ||
    data.non_field_errors;

  if (!message) return '';
  return typeof message === 'string' ? message : JSON.stringify(message);
};

/**
 * Reads a blob response body and extracts a useful error message.
 * @param {Blob} blob Blob that may contain JSON or text.
 * @returns {Promise<string>} Parsed message or plain text, if present.
 */
const getMessageFromBlob = async (blob) => {
  if (!(blob instanceof Blob)) return '';
  const text = await blob.text();
  if (!text) return '';

  try {
    const parsed = JSON.parse(text);
    return getMessageFromObject(parsed) || text;
  } catch (_error) {
    return text;
  }
};

/**
 * Resolves the best user-facing message from an Axios download error.
 * @param {Error|object} error Axios error or thrown error.
 * @returns {Promise<string>} Message suitable for an error toast.
 */
const getDownloadErrorMessage = async (error) => {
  const responseData = error?.response?.data;

  if (responseData instanceof Blob) {
    const blobMessage = await getMessageFromBlob(responseData);
    if (blobMessage) return blobMessage;
  }

  const objectMessage = getMessageFromObject(responseData);
  if (objectMessage) return objectMessage;

  return error?.message || 'Failed to download file.';
};

/**
 * Normalizes an Axios response into a non-empty downloadable blob.
 * @param {import('axios').AxiosResponse} response Axios response from a blob request.
 * @returns {Promise<Blob>} Valid non-empty blob.
 * @throws {Error} When the blob is empty or contains JSON error content.
 */
const getBlobFromResponse = async (response) => {
  const blob =
    response?.data instanceof Blob
      ? response.data
      : new Blob([response?.data], {
          type: getHeader(response?.headers, 'content-type'),
        });

  if (!blob || blob.size === 0) {
    throw new Error('Downloaded file is empty.');
  }

  const contentType = getHeader(response?.headers, 'content-type') || blob.type;
  if (contentType?.toLowerCase()?.includes('application/json')) {
    const message = await getMessageFromBlob(blob);
    throw new Error(message || 'Failed to download file.');
  }

  return blob;
};

/**
 * Downloads a file returned by a backend endpoint as a browser download.
 *
 * Calls the provided URL with Axios using `responseType: 'blob'`, validates that
 * required values are present before making the request, extracts a filename from
 * the `Content-Disposition` response header when available, and falls back to the
 * supplied filename otherwise. The resolved blob is downloaded through a temporary
 * object URL, and that URL is always revoked during cleanup.
 *
 * Error handling:
 * - Missing required fields show `missingMessage` and skip the API call.
 * - Empty blob responses show an error toast.
 * - JSON error payloads returned as blobs are parsed for a meaningful message.
 * - Axios/interceptor duplicate error toasts are suppressed for this request.
 *
 * @param {object} options Download options.
 * @param {string} options.url Backend endpoint to call.
 * @param {object} [options.params] Query params to send with the request.
 * @param {string} options.fallbackFileName Filename used when the backend does not send one.
 * @param {{label: string, value: unknown}[]} [options.missingFields] Required values to validate before calling the API.
 * @param {string} [options.missingMessage] Toast message shown when any required value is missing.
 * @returns {Promise<import('axios').AxiosResponse<Blob>|null>} Axios response when the download starts successfully; otherwise `null`.
 */
export const downloadBackendFile = async ({
  url,
  params,
  fallbackFileName,
  missingFields = [],
  missingMessage = 'Required download details are missing.',
}) => {
  if (missingFields.some(({ value }) => isMissing(value))) {
    SwalToast({ icon: 'error', title: missingMessage });
    return null;
  }

  let objectUrl = '';
  try {
    const response = await axios.get(url, {
      params,
      responseType: 'blob',
      suppressErrorToast: true,
    });
    const blob = await getBlobFromResponse(response);
    const headerFileName = getFileNameFromContentDisposition(
      getHeader(response?.headers, 'content-disposition')
    );
    const fileName = headerFileName || fallbackFileName;

    objectUrl = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = objectUrl;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    return response;
  } catch (error) {
    const errorMessage = await getDownloadErrorMessage(error);
    SwalToast({ icon: 'error', title: errorMessage });
    return null;
  } finally {
    if (objectUrl) {
      window.URL.revokeObjectURL(objectUrl);
    }
  }
};

/**
 * Downloads a ModelInventory HTML attribute as a PDF from the model attribute
 * download endpoint.
 *
 * The backend owns HTML-to-PDF generation. This helper only validates the model
 * entity id, entity type, and attribute id, requests the backend blob with
 * `download_format=pdf`, and delegates filename handling, object URL download,
 * cleanup, and blob/error parsing to `downloadBackendFile`.
 *
 * @param {object} options Download options for the model HTML attribute.
 * @param {string|number} options.entityId Current model/entity id.
 * @param {string|number} options.attributeId HTML attribute id.
 * @param {string} options.attributeLabel Attribute label/name used for the fallback filename.
 * @returns {Promise<import('axios').AxiosResponse<Blob>|null>} Axios response when the download starts successfully; otherwise `null`.
 */
export const downloadModelHtmlAttributePdf = ({
  entityId,
  attributeId,
  attributeLabel,
}) => {
  const downloadFormat = 'pdf';
  const entityType = 'ModelInventory';

  return downloadBackendFile({
    url: `${API_ENDPOINTS.MODULE_BASE_URL.AUTH}${API_ENDPOINTS.CNF.ATTR.HTML_ATTRIBUTE_DOWNLOAD}`,
    params: {
      attribute_id: attributeId,
      download_format: downloadFormat,
      entity_type: entityType,
      entity_id: entityId,
    },
    fallbackFileName: buildFallbackFileName(attributeLabel, downloadFormat),
    missingFields: [
      { label: 'Attribute id', value: attributeId },
      { label: 'Download format', value: downloadFormat },
      { label: 'Entity type', value: entityType },
      { label: 'Entity id', value: entityId },
    ],
    missingMessage:
      'Unable to download PDF: model id, entity type, attribute id, or download format is missing.',
  });
};

/**
 * Downloads an HTML attribute template file from the custom-entity attribute
 * download endpoint in the requested backend-supported format.
 *
 * Supported formats for MV-10164 are `pdf`, `html`, and `txt`. This helper does
 * not convert files in the browser; it validates the attribute id, calls the
 * backend with the selected `download_format`, and delegates blob download behavior,
 * filename resolution, empty blob handling, JSON error blob parsing, and object
 * URL cleanup to `downloadBackendFile`.
 *
 * @param {object} options Download options for the custom HTML attribute file.
 * @param {string|number} options.attributeId Attribute id used in the endpoint path.
 * @param {string} options.attributeLabel Attribute label/name used for the fallback filename.
 * @param {'pdf'|'html'|'txt'} options.format Backend download format.
 * @returns {Promise<import('axios').AxiosResponse<Blob>|null>} Axios response when the download starts successfully; otherwise `null`.
 */
export const downloadCustomHtmlAttributeFile = ({
  attributeId,
  attributeLabel,
  format,
}) =>
  downloadBackendFile({
    url: `${API_ENDPOINTS.MODULE_BASE_URL.AUTH}${API_ENDPOINTS.CNF.ATTR.HTML_ATTRIBUTE_DOWNLOAD}`,
    params: {
      attribute_id: attributeId,
      download_format: format,
    },
    fallbackFileName: buildFallbackFileName(attributeLabel, format),
    missingFields: [
      { label: 'Attribute id', value: attributeId },
      { label: 'Download format', value: format },
    ],
    missingMessage:
      'Unable to download file: attribute id or download format is missing.',
  });
